import { FormStore } from '@roc/feature-app-core';
import { GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { InstaQuoteRanges } from '../../utils/types';
import { LoanEconomicsStore } from './loanEconomicsStore';
import { sum } from '../../utils/utils';

import { isFixAndFlip, isStabilizedBridge } from '@roc/feature-utils';

const leverageForm = {
  fields: {
    loanToCost: {
      value: 0,
      error: null,
      rule: '',
    },
    financedIrPercentage: {
      value: 0,
      error: null,
      rule: '',
    },
    constructionReserve: {
      value: 0,
      error: null,
      rule: '',
    },
    subMarketLevel: {
      value: 0,
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

const LEVERAGE_EXCEPTION_RANGES = {
  minLoanToCost: 0,
  maxLoanToCost: 100,
  minConstructionReserve: 0,
  maxConstructionReserve: 100,
  maxHighFicoLtc: 90,
};

export class LeverageFormStore extends FormStore {
  public loanEconomicsStore: LoanEconomicsStore;

  instaQuote: boolean;
  propertyFormHash: string;

  constructor(
    globalStore: GlobalStore,
    leverageStore: LoanEconomicsStore,
    instaQuote: boolean
  ) {
    super(leverageForm, globalStore);
    this.loanEconomicsStore = leverageStore;
    this.instaQuote = instaQuote;
    this.propertyFormHash = '';

    makeObservable(this, {
      calculatedFields: computed,
      ranges: computed,
      propertyFormHash: observable,
      setPropertyFormHash: action,
    });

  }

  reset() {
    super.reset();
    this.propertyFormHash = '';
  }
  calculateMaxLTCForHighFico(ranges: InstaQuoteRanges) {
    const isHighLeverageBox = this.loanEconomicsStore.instaQuoteRanges?.highLeverageBox;
    if (!isHighLeverageBox) {
      return ranges.maxLoanToCost;
    }
    else {
      const properties = this.loanEconomicsStore.quoteTypeStore.propertiesStore.getProperties();
      let tempMaxLTC = ranges?.maxHighFicoLtc
      this.onFieldChange('loanToCost', tempMaxLTC);
      const constructionReserve = this.form.fields.constructionReserve.value
      const totalConstructionRenoSpendToDate = this.loanEconomicsStore?.quoteTypeStore.isMidConstructionRefinance() ?
        sum(properties.map(p => p.constructionRenoSpendToDate)) : 0;

      const totalSellerCredit = sum(properties.filter(p => !!p.sellerCredit).map(p => p.sellerCreditAmount));
      const totalPurchasePrice = sum(properties.map(p => Number(p.purchasePrice))) + totalConstructionRenoSpendToDate - totalSellerCredit;
      const totalProposedConstructionBudget = sum(
        properties.map(p => Number(p.renovationBudget))
      );
      const constructionHoldback =
        totalProposedConstructionBudget * (constructionReserve / 100);
      const totalLoanAmount = this.loanEconomicsStore.calculatedFields?.totalLoanAmount;
      // if total loan amount is more that high fico allowed amount, we will set max high fico as total
      // and calculate ltc % for that.
      if (ranges?.maxHighLeverageLoanAmount < totalLoanAmount) {
        const initialAdvance = ranges?.maxHighLeverageLoanAmount - constructionHoldback;
        tempMaxLTC = Math.floor((initialAdvance / totalPurchasePrice) * 100);
      }
      this.onFieldChange('loanToCost', tempMaxLTC);
      return tempMaxLTC;
    }
  }
  loadRanges(ranges: InstaQuoteRanges) {
    const values = this.getFormValues();
    let loanToCost = values.loanToCost > 0
      ? values.loanToCost
      : ranges.defaultLoanToCost;

    loanToCost = Math.max(loanToCost, ranges.minLoanToCost);
    loanToCost = Math.min(loanToCost, ranges.maxLoanToCost);
    if (loanToCost < 0) {
      loanToCost = 0;
    }
    if (isStabilizedBridge(this.loanEconomicsStore.quoteTypeStore.loanSubtype) &&
      this.instaQuote) {
      loanToCost = Math.min(
        loanToCost,
        this.calculateMaxLTCBasedOnLTV(ranges));
    }
    if (isFixAndFlip(this.loanEconomicsStore.quoteTypeStore.loanSubtype)) {
      loanToCost = this.calculateMaxLTCForHighFico(ranges);
    }

    let constrReserve = values.constructionReserve > 0
      ? values.constructionReserve
      : ranges.defaultConstructionReserve;

    constrReserve = Math.max(constrReserve, ranges.minConstructionReserve);
    constrReserve = Math.min(constrReserve, ranges.maxConstructionReserve);
    constrReserve = Math.min(
      constrReserve,
      this.calculateMaxConstuctionReserve(ranges, loanToCost)
    );
    this.onFieldChange('loanToCost', loanToCost);
    this.onFieldChange('constructionReserve', constrReserve);
    this.onFieldChange('subMarketLevel', ranges.submarketLevel);
  }

  private calculateMaxLTCBasedOnLTV(ranges: InstaQuoteRanges) {
    const totalPurchasePrice = this.calculatedFields.totalPurchasePrice;
    const totalMarketValue = this.calculatedFields.totalMarketValue;

    if (!totalPurchasePrice || !totalMarketValue || !ranges.maxARLTV) {
      return 100;
    }

    const maxLTVLoanAmount = totalMarketValue * (ranges.maxARLTV / 100);
    const maxLTC = (maxLTVLoanAmount) / totalPurchasePrice * 100;

    return Math.min(100, Math.floor(maxLTC));
  }

  private calculateMaxConstuctionReserve(ranges, loanToCost) {
    const totalPurchasePrice = this.calculatedFields.totalPurchasePrice;
    const totalAfterRepairValue = this.calculatedFields.totalAfterRepairValue;
    const totalProposedConstructionBudget = this.calculatedFields
      .totalProposedConstructionBudget;

    const initialLoanAmount = totalPurchasePrice * (loanToCost / 100);

    let maxCRbasedOnARLTV = 100;
    if (ranges.maxARLTV) {
      const maxARLTVLoanAmount =
        totalAfterRepairValue * (ranges.maxARLTV / 100);
      maxCRbasedOnARLTV = Math.floor(
        ((maxARLTVLoanAmount - initialLoanAmount) /
          totalProposedConstructionBudget) *
        100
      );
    }

    let maxCRbasedOnLTFC = 100;
    if (ranges.maxLTFC) {
      const totalCost = totalPurchasePrice + totalProposedConstructionBudget;
      const maxLTFCLoanAmount = totalCost * (ranges.maxLTFC / 100);
      maxCRbasedOnLTFC = Math.floor(
        ((maxLTFCLoanAmount - initialLoanAmount) /
          totalProposedConstructionBudget) *
        100
      );
    }

    return Math.min(maxCRbasedOnARLTV, maxCRbasedOnLTFC);
  }

  get calculatedFields() {
    const properties = this.loanEconomicsStore.quoteTypeStore.propertiesStore.getProperties();
    const loanToCost = this.form.fields.loanToCost.value;
    const financedIrPercentage = this.form.fields.financedIrPercentage.value;
    let constructionReserve = this.form.fields.constructionReserve.value;
    //Ensure construction reserve is not negative
    if (constructionReserve < 0) {
      constructionReserve = 0;
      this.onFieldChange('constructionReserve', constructionReserve);
    }

    const calculatedFields = this.loanEconomicsStore.caclulateFields(properties, {
      loanToCost,
      financedIrPercentage,
      constructionReserve,
      propertyFormHash: this.propertyFormHash,
    });

    if (this.instaQuote) {
      this.loanEconomicsStore.quoteTypeStore.saveSuggestedQuoteIfEligible({
        ...calculatedFields,
        loanToCost,
      });
    }

    return calculatedFields;
  }

  get ranges() {
    return this.instaQuote
      ? this.loanEconomicsStore.instaQuoteRanges
      : LEVERAGE_EXCEPTION_RANGES;
  }

  setPropertyFormHash(propertyFormHash: string) {
    this.propertyFormHash = propertyFormHash;
  }
}
